import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  AGENT_ID_QUERY_KEY,
  AgentCentralizedContext,
} from '~/features/agents/constants'
import useGetAgentsByOrgId from '~/features/agents/hooks/useGetAgentsByOrgId'
import useGetGuestAgent from '~/features/agents/hooks/useGetGuestAgent'
import { useSetAgentId } from '~/features/agents/hooks/useSetAgentId'
import {
  EAgentKeys,
  EAgentStatus,
  TAgent,
  TAgentCentralizedContext,
} from '~/features/agents/models'
import {
  getActiveAgents,
  getDefaultAgentData,
  getFirstActiveAgent,
  isAgentActive,
} from '~/features/agents/utils'
import useHasValidAuthSession from '~/features/auth/hooks/useHasValidAuthSession'
import useGetAuthSessionQuery from '~/hooks/useGetAuthSessionQuery'
import { sessionStore, store } from '~/store'

function GetAgentCentralizedProvider({ children = null }: PropsWithChildren) {
  const { data: authSession } = useGetAuthSessionQuery()
  const { isLoggedIn } = useHasValidAuthSession()
  const userOrgId = store((state) => state.user.organizationId)
  const [stateAgentId] = sessionStore((state) => [state.agent.id || ''])
  const [searchParams] = useSearchParams()
  const searchParamsAgentId = searchParams.get(AGENT_ID_QUERY_KEY)
  const setAgentId = useSetAgentId()

  const {
    data: agents,
    isSuccess: isGetAgentsSuccessful,
    isLoading: isLoadingGetAgents,
    isError,
  } = useGetAgentsByOrgId({
    orgId: userOrgId,
    queryOptions: {
      refetchOnWindowFocus: (query) => {
        const hasError = query.state.status === 'error'

        return !hasError
      },
    },
  })

  // Disabled while the org-scoped fetch is the active path; otherwise the
  // agent id from the URL or session (guest flows only).
  const guestLookupAgentId =
    userOrgId && !isError ? '' : searchParamsAgentId || stateAgentId

  const {
    data: guestAgentById,
    isSuccess: isGetGuestAgentByIdSuccessful,
    isError: isGuestAgentByIdIsError,
  } = useGetGuestAgent({
    agentId: !isLoggedIn ? guestLookupAgentId : '',
  })

  const agentId =
    searchParamsAgentId ||
    stateAgentId ||
    getFirstActiveAgent(agents?.data || [])?.[EAgentKeys.id] ||
    ''

  useEffect(() => {
    if (!isGetAgentsSuccessful || !authSession?.cognitoUid) {
      return
    }

    if (!agents?.data?.length) {
      if (stateAgentId || searchParamsAgentId) {
        setAgentId({ agentId: '' })
      }

      toast.error('No agents found', { toastId: 'no-agents-found' })
      return
    }

    if (!searchParamsAgentId) {
      if (!stateAgentId) {
        const firstActiveAgent = getFirstActiveAgent(agents.data)
        const selectedAgentId = firstActiveAgent?.id || ''
        setAgentId({ agentId: selectedAgentId })
      } else {
        const correspondingAgentIdFromState =
          agents.data.find(
            (agent) => agent.id === stateAgentId && isAgentActive(agent),
          )?.id || ''
        setAgentId({ agentId: correspondingAgentIdFromState })
      }

      return
    }

    if (!stateAgentId || stateAgentId !== searchParamsAgentId) {
      setAgentId({ agentId: searchParamsAgentId })
    }
  }, [
    agents,
    isGetAgentsSuccessful,
    stateAgentId,
    setAgentId,
    searchParamsAgentId,
    authSession,
  ])

  useEffect(() => {
    if (agents?.data?.length) {
      const agentNotInOrg = !agents?.data?.find((agent) => agent.id === agentId)
      if (agentNotInOrg) {
        setAgentId({ agentId: '' })
      }
    }
  }, [agentId, agents?.data, setAgentId])

  let isLoading = false

  if (userOrgId) {
    isLoading = isLoadingGetAgents && !isGetAgentsSuccessful
  } else if (!isLoggedIn && (searchParamsAgentId || stateAgentId)) {
    isLoading = !isGetGuestAgentByIdSuccessful
  } else if (!isLoggedIn) {
    isLoading = !isGetAgentsSuccessful && !isGetGuestAgentByIdSuccessful
  }

  const selectedAgent: TAgent | undefined = useMemo(() => {
    const hasSession = !!authSession?.cognitoUid

    if (hasSession) {
      const agentsData = agents?.data || []

      const existingAgentData =
        agents?.data?.find(
          (agent) => agent.id === agentId && isAgentActive(agent),
        ) || getFirstActiveAgent(agentsData)

      return getDefaultAgentData(existingAgentData)
    }

    const agent = guestAgentById?.data

    // Fail open on an unknown status rather than isAgentActive's strict
    // === active check: agentStatus only exists on this guest payload as of
    // this change (see publicGuestAgentFields in heyLibbyAgent), so until
    // that backend deploy lands, it comes back undefined for every guest.
    // Treating undefined as active avoids every guest seeing a blank agent
    // for the length of that deploy gap; an explicitly disabled/locked
    // status is still excluded exactly as before.
    const isGuestAgentUsable =
      !!agent &&
      agent.agentStatus !== EAgentStatus.disabled &&
      agent.agentStatus !== EAgentStatus.lockedDisabled

    return getDefaultAgentData(isGuestAgentUsable ? agent : undefined)
  }, [guestAgentById?.data, agentId, agents?.data, authSession?.cognitoUid])

  const activeAgents = useMemo(
    () => getActiveAgents(agents?.data),
    [agents?.data],
  )

  const getAgentById = useCallback(
    (lookupAgentId: string): TAgent | null => {
      if (!lookupAgentId) {
        return null
      }

      const fromList = agents?.data?.find(
        (agent) => agent[EAgentKeys.id] === lookupAgentId,
      )

      return fromList ? getDefaultAgentData(fromList) : null
    },
    [agents?.data],
  )

  const agentCentralizedContextValue = useMemo<TAgentCentralizedContext>(() => {
    return {
      selectedAgent,
      agents: agents?.data,
      activeAgents,
      getAgentById,
      isError: userOrgId ? isError : isGuestAgentByIdIsError,
      isSuccess: userOrgId
        ? isGetAgentsSuccessful
        : isGetGuestAgentByIdSuccessful,
      isLoading,
    }
  }, [
    activeAgents,
    agents?.data,
    getAgentById,
    isError,
    isGetGuestAgentByIdSuccessful,
    isGetAgentsSuccessful,
    isGuestAgentByIdIsError,
    isLoading,
    selectedAgent,
    userOrgId,
  ])

  return (
    <AgentCentralizedContext.Provider value={agentCentralizedContextValue}>
      {children}
    </AgentCentralizedContext.Provider>
  )
}

export default GetAgentCentralizedProvider
